import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { API_URL } from "../../config";

/**
 * Shared preview cell for any Company Settings brand-asset field.
 *
 * Sizing contract (identical across Primary Logo / Email Logo / Login Logo
 * / Secondary Logo / Print Logo previews):
 *   - max-width  : 320px
 *   - max-height : 100px  (default; caller can override for square favicon
 *                 or portrait print logo)
 *   - width/height: auto
 *   - object-fit : contain
 *   - Preserves aspect ratio — never crops, never stretches.
 *
 * Missing/broken source → renders a neutral placeholder icon + label so
 * a fresh tenant never sees a broken-image chrome. Broken images do not
 * throw; the onError handler falls through to the placeholder state.
 */
type Props = {
  /** Absolute URL, storage-relative path, or empty. Empty renders placeholder. */
  src?: string | null;
  /** Alt text — use the tenant's company name where available. */
  alt?: string;
  /** Override the default 100 px max-height (e.g. favicons: 64). */
  maxHeightPx?: number;
  /** Override the default 320 px max-width. */
  maxWidthPx?: number;
  /** Optional label rendered under the preview (e.g. "Login Logo"). */
  caption?: string;
};

/**
 * Turn a stored path into a public URL. Absolute URLs pass through.
 * A blob: URL from a fresh file input also passes through.
 */
const resolveSrc = (src?: string | null): string => {
  if (!src) return "";
  const s = String(src).trim();
  if (s === "") return "";
  if (/^(https?:|blob:|data:)/i.test(s)) return s;
  // Storage-relative path — prepend API host so the browser can fetch it.
  const base = String(API_URL || "").replace(/\/(api\/?)?$/i, "");
  return `${base}/${s.replace(/^\/+/, "")}`;
};

const LogoPreview: React.FC<Props> = ({
  src,
  alt = "Company Logo",
  maxHeightPx = 100,
  maxWidthPx = 320,
  caption,
}) => {
  const [broken, setBroken] = useState(false);
  const resolved = resolveSrc(src);
  const showPlaceholder = !resolved || broken;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 0.75,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: maxHeightPx,
          minWidth: 120,
          maxWidth: "100%",
          px: 1.5,
          py: 1,
          bgcolor: "#fff",
          border: "1px solid rgba(15,23,42,0.08)",
          borderRadius: 1.5,
        }}
      >
        {showPlaceholder ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "text.disabled",
            }}
          >
            <ImageOutlinedIcon fontSize="small" />
            <Typography variant="caption">No image</Typography>
          </Box>
        ) : (
          <Box
            component="img"
            src={resolved}
            alt={alt}
            onError={() => setBroken(true)}
            sx={{
              width: "auto",
              height: "auto",
              maxWidth: `${maxWidthPx}px`,
              maxHeight: `${maxHeightPx}px`,
              objectFit: "contain",
              display: "block",
            }}
          />
        )}
      </Box>
      {caption && (
        <Typography variant="caption" sx={{ color: "text.disabled" }}>
          {caption}
        </Typography>
      )}
    </Box>
  );
};

export default LogoPreview;
